import { commaFormatCurrency } from '@ltvco/refresh-lib/utils';

export const formatCurrencyRange = (min?: number, max?: number) => {
  // If min is negative, clamp it to 0 for display purposes
  const adjustedMin = min !== undefined && min < 0 ? 0 : min;
  const adjustedMax = max !== undefined && max < 0 ? 0 : max;
  const effectiveMin =
    adjustedMin !== undefined &&
    adjustedMax !== undefined &&
    adjustedMin > adjustedMax
      ? adjustedMax
      : adjustedMin;
  const effectiveMax =
    adjustedMin !== undefined &&
    adjustedMax !== undefined &&
    adjustedMin > adjustedMax
      ? adjustedMin
      : adjustedMax;
  const minFormatted = commaFormatCurrency(effectiveMin);
  const maxFormatted = commaFormatCurrency(effectiveMax);

  if (effectiveMax === 0 && effectiveMin === 0) {
    return '$0';
  }

  if (minFormatted && maxFormatted) {
    return `${minFormatted} - ${maxFormatted}`;
  }

  return minFormatted || maxFormatted || null;
};

export const formatPercent = (value?: number) => {
  if (typeof value !== 'number') {
    return null;
  }

  return `${value}%`;
};

export const formatCurrencyWithSign = (value?: number) => {
  if (typeof value !== 'number') {
    return null;
  }

  const formatted = commaFormatCurrency(value);
  if (!formatted) {
    return null;
  }

  // If positive, add + sign
  if (value > 0) {
    return `+${formatted}`;
  }

  // Negative values already have the - sign from commaFormatCurrency
  return formatted;
};

/**
 * Finds the scrollable container element by traversing up the DOM tree
 */
export const findScrollContainer = (
  element: HTMLElement | null,
): HTMLElement | null => {
  if (!element) return null;

  let current: HTMLElement | null = element;

  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const hasOverflow =
      (style.overflowY === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflow === 'auto' ||
        style.overflow === 'scroll') &&
      current.scrollHeight > current.clientHeight;

    if (hasOverflow) {
      return current;
    }

    // Also check parent
    if (current.parentElement) {
      const parentStyle = window.getComputedStyle(current.parentElement);
      const parentHasOverflow =
        (parentStyle.overflowY === 'auto' ||
          parentStyle.overflowY === 'scroll' ||
          parentStyle.overflow === 'auto' ||
          parentStyle.overflow === 'scroll') &&
        current.parentElement.scrollHeight > current.parentElement.clientHeight;

      if (parentHasOverflow) {
        return current.parentElement;
      }
    }

    current = current.parentElement;
  }

  return null;
};

/**
 * Finds header element by searching for text content
 */
export const findHeaderElement = (
  searchText: string,
  startElement: HTMLElement,
): HTMLElement | null => {
  const searchInElement = (el: HTMLElement): HTMLElement | null => {
    if (el.textContent?.includes(searchText)) {
      return el;
    }
    for (const child of Array.from(el.children) as HTMLElement[]) {
      const found = searchInElement(child);
      if (found) return found;
    }
    return null;
  };

  // Search in start element
  let headerElement = searchInElement(startElement);

  // If not found, search in document
  if (!headerElement) {
    const allElements = document.querySelectorAll('*');
    for (const el of Array.from(allElements)) {
      if (el.textContent?.includes(searchText)) {
        return el as HTMLElement;
      }
    }
  }

  return headerElement;
};

/**
 * Calculates scroll position to show element below header
 */
export const calculateScrollPosition = (
  scrollContainer: HTMLElement,
  headerElement: HTMLElement | null,
  targetElement: HTMLElement | null,
): number => {
  const containerRect = scrollContainer.getBoundingClientRect();
  const defaultPosition = 100;

  if (headerElement && targetElement) {
    // Calculate position of target element relative to scroll container
    const targetRect = targetElement.getBoundingClientRect();
    return targetRect.top - containerRect.top + scrollContainer.scrollTop;
  }

  if (headerElement) {
    // Scroll just below header
    const headerRect = headerElement.getBoundingClientRect();
    return headerRect.bottom - containerRect.top + scrollContainer.scrollTop;
  }

  if (targetElement) {
    // Scroll to target element
    const targetRect = targetElement.getBoundingClientRect();
    return targetRect.top - containerRect.top + scrollContainer.scrollTop;
  }

  return defaultPosition;
};

/**
 * Message pair interface for sticky header calculation
 */
export interface MessagePair {
  userIndex: number;
  assistantIndex: number | null;
}

/**
 * Calculates the sticky index based on visible assistant messages
 */
export const calculateStickyIndex = (
  visibleAssistantIndexes: Set<number>,
  pairs: MessagePair[],
): number | null => {
  if (visibleAssistantIndexes.size === 0) {
    return null;
  }

  const visibleUserMessageIndexes = Array.from(visibleAssistantIndexes)
    .map((visibleAssistantIndex) => {
      const messagePair = pairs.find(
        (pair) => pair.assistantIndex === visibleAssistantIndex,
      );
      return messagePair ? messagePair.userIndex : null;
    })
    .filter((userIndex): userIndex is number => userIndex !== null);

  if (visibleUserMessageIndexes.length === 0) {
    return null;
  }

  return Math.min(...visibleUserMessageIndexes);
};

/**
 * Calculates the tail gap (spacer) to anchor the last message pair to the top
 */
export const calculateTailGap = (
  containerHeight: number,
  userElementHeight: number,
  assistantElementHeight: number | null,
  gapElementHeight: number,
  typingElementHeight: number | null,
  isLoading: boolean,
  maxTailGap: number,
): number => {
  let secondHeight = 0;

  if (assistantElementHeight !== null) {
    secondHeight = Math.max(0, assistantElementHeight - gapElementHeight);
  } else if (isLoading && typingElementHeight !== null) {
    secondHeight = typingElementHeight;
  }

  const pairHeight = userElementHeight + secondHeight;
  const availableHeight = Math.max(0, containerHeight);
  const gap = Math.max(0, availableHeight - pairHeight);

  return Math.min(maxTailGap, gap);
};

export const getSeverityColor = (severityValue?: string): string => {
  if (!severityValue) {
    return 'text.primary';
  }
  const normalizedSeverity = severityValue.toLowerCase();
  if (normalizedSeverity === 'minor') {
    return 'warning.light';
  }
  if (normalizedSeverity === 'moderate') {
    return 'warning.dark';
  }
  if (normalizedSeverity === 'severe') {
    return 'error.main';
  }
  return 'text.primary';
};

export const getPostRepairValueColor = (value?: number): string => {
  if (typeof value !== 'number') {
    return 'text.primary';
  }
  return value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.primary';
};

export const getNetGainColor = (min?: number, max?: number): string => {
  // Check the maximum value to determine if overall positive
  if (typeof max === 'number' && max > 0) {
    return 'success.main';
  }
  // If max is negative or zero, check if min is also negative
  if (
    typeof min === 'number' &&
    min < 0 &&
    (typeof max !== 'number' || max <= 0)
  ) {
    return 'error.main';
  }
  return 'text.primary';
};
